import React from 'react';
// 🟢 [修复] 合并引入：Navigate, useLocation, useNavigate 都在这一行
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { motion } from 'framer-motion';

const PrivateRoute = ({ children, allowedRoles = [], requireAuth = true }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-100 via-sky-200 to-blue-300">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="w-16 h-16 border-4 border-white border-t-transparent rounded-full"
        />
      </div>
    );
  }

  // Check if authentication is required
  if (requireAuth && !user) {
    // Redirect to login page with return url
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user has required role
  if (allowedRoles.length > 0 && user && !allowedRoles.includes(user.role)) {
    return <UnauthorizedPage userRole={user.role} requiredRoles={allowedRoles} />;
  }

  // Check if user account is approved
  if (user && user.status !== 'approved') {
    return <PendingApprovalPage />;
  }

  return children;
};

// Unauthorized access page
const UnauthorizedPage = ({ userRole, requiredRoles }) => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-100 via-sky-200 to-blue-300 p-6">
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className="bg-white bg-opacity-20 backdrop-blur-lg rounded-2xl p-8 text-center max-w-md mx-auto"
    >
      <div className="text-6xl mb-4">🚫</div>
      <h1 className="text-3xl font-bold text-gray-800 mb-4">
        Access Denied
      </h1>
      <p className="text-gray-700 mb-4">
        You don't have permission to access this page.
      </p>
      <div className="text-sm text-gray-600 mb-6">
        <p>Your role: <span className="font-semibold">{userRole}</span></p>
        <p>Required roles: <span className="font-semibold">{requiredRoles.join(', ')}</span></p>
      </div>
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => window.history.back()}
        className="bg-white text-purple-600 hover:bg-gray-100 font-bold py-3 px-6 rounded-lg"
      >
        Go Back
      </motion.button>
    </motion.div>
  </div>
);

// Pending approval page
const PendingApprovalPage = () => {
  const navigate = useNavigate(); // 🟢 [新增] 使用 Hook 进行跳转

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-100 via-sky-200 to-blue-300 p-6">
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white bg-opacity-20 backdrop-blur-lg rounded-2xl p-8 text-center max-w-md mx-auto"
      >
        <div className="text-6xl mb-4">⏳</div>
        <h1 className="text-3xl font-bold text-gray-800 mb-4">
          Account Pending Approval
        </h1>
        <p className="text-gray-700 mb-6">
          Your account is waiting for administrator approval. You'll receive an email once your account is activated.
        </p>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          // 🟢 [修改] 使用 navigate 替代 window.location.href，避免页面白屏刷新
          onClick={() => navigate('/')}
          className="bg-white text-purple-600 hover:bg-gray-100 font-bold py-3 px-6 rounded-lg"
        >
          Back to Home
        </motion.button>
      </motion.div>
    </div>
  );
};

export default PrivateRoute;